import React, { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@roger/ui-components';
import { getGoalOptionsForVisit, client } from '../../services/api';
import type { GoalTemplateOption } from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import GoalOptionCard from './GoalOptionCard';

interface AddGoalsBoxProps {
  open: boolean;
  onClose: () => void;
  visitId: string;
  onAddGoals?: (goalTemplateIds: string[]) => void;
  onUseFreeText?: () => void;
}

const AddGoalsBox: React.FC<AddGoalsBoxProps> = ({
  open,
  onClose,
  visitId,
  onAddGoals,
  onUseFreeText,
}) => {
  const [goalOptions, setGoalOptions] = useState<GoalTemplateOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { error: showError } = useToast();

  // Resets all component state when the dialog is closed. IMPORTANT: When adding
  // new state variables, add them to this function so the component starts fresh
  // each time it's opened.
  const resetAddGoalsBoxState = () => {
    setGoalOptions([]);
    setSelectedIds(new Set());
    setSearchQuery('');
    setIsLoading(false);
    setHasError(false);
  };

  useEffect(() => {
    if (!open) {
      resetAddGoalsBoxState();
    }
  }, [open]);

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleUseFreeText = () => {
    onUseFreeText?.();
    onClose();
  };

  const handleContinue = () => {
    onAddGoals?.(Array.from(selectedIds));
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    setSelectedIds(new Set());
    setSearchQuery('');
    setHasError(false);
    setIsLoading(true);
    getGoalOptionsForVisit({
      path: { visit_id: visitId },
      client,
      throwOnError: true,
    })
      .then(response => {
        setHasError(false);
        setGoalOptions(response.data ?? []);
      })
      .catch(() => {
        setHasError(true);
        showError(
          'Failed to load goal options',
          'Please close the goals tab and try again'
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [open, visitId]);

  const filteredGoalOptions = goalOptions.filter(option =>
    option.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='max-w-2xl h-[68vh] flex flex-col overflow-hidden p-3 gap-2'>
        <DialogHeader className='flex-shrink-0 space-y-0.5'>
          <DialogTitle className='text-base'>
            Select one or more goals to add
          </DialogTitle>
          {(isLoading || hasError || goalOptions.length === 0) && (
            <p className='text-xs text-muted-foreground'>
              {isLoading
                ? 'Loading goals...'
                : hasError
                  ? 'Please try again. If the issue persists, contact your administrator'
                  : 'There are no goals available for this visit. Please contact your administrator'}
            </p>
          )}
        </DialogHeader>
        {!isLoading && !hasError && goalOptions.length > 0 && (
          <div className='relative'>
            <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder='Search goals...'
              inputSize='sm'
              className='pl-8'
            />
          </div>
        )}
        <div className='flex-1 min-h-0 overflow-y-auto pr-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
          {isLoading ? (
            <div className='flex-1 flex items-center justify-center'>
              <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            </div>
          ) : hasError ? null : goalOptions.length > 0 ? (
            <div className='space-y-1.5 pr-1'>
              {filteredGoalOptions.map(option => (
                <GoalOptionCard
                  key={option.id}
                  goalOption={option}
                  selected={selectedIds.has(option.id)}
                  onClick={() => toggleSelection(option.id)}
                />
              ))}
            </div>
          ) : null}
          {!isLoading &&
            !hasError &&
            goalOptions.length > 0 &&
            filteredGoalOptions.length === 0 && (
              <p className='py-8 text-center text-sm text-muted-foreground'>
                No matching goals
              </p>
            )}
        </div>
        <DialogFooter className='flex-shrink-0 w-full grid grid-cols-[1fr_auto] items-center'>
          <Button
            variant='outline'
            size='sm'
            className='justify-self-start'
            onClick={handleUseFreeText}
          >
            Use Free Text
          </Button>
          <div className='flex gap-2'>
            <Button variant='outline' size='sm' onClick={onClose}>
              Cancel
            </Button>
            <Button
              size='sm'
              onClick={handleContinue}
              disabled={selectedIds.size === 0}
            >
              Continue ({selectedIds.size})
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddGoalsBox;
